// File: frontend/src/views/public/Contact.js

import React, { useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { Mail, MessageCircle, Send } from 'lucide-react';
import ContactForm from '../../components/forms/ContactForm';
import ScrollAnimation from '../../components/common/ScrollAnimation';
import PageContainer from '../../components/layout/PageContainer';
import styles from './Contact.module.css';
import SEO from '../../components/utility/SEO';
import { SEO_CONFIG } from '../../config/seo';

const Contact = () => {
  const { t } = useLanguage();
  const seo = SEO_CONFIG.contact;

  const handleChatClick = () => {
    window.dispatchEvent(new Event('openChatWidget'));
  };

  return (
    <>
      <SEO 
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
      />
      <PageContainer>
        <div className={styles.page}>
          {/* Hero Section */}
          <section className={styles.hero}>
            <ScrollAnimation>
              <h1 className={styles.heroTitle}>
                {t('contact.hero.title', 'We\'re Here to Help')}
              </h1>
              <p className={styles.heroSubtitle}>
                {t('contact.hero.subtitle', 'Questions about your policies? Need help getting started? Our team is ready to assist you.')}
              </p>
            </ScrollAnimation>
          </section>

          {/* Section 1: Contact Form (Content LEFT, Image RIGHT) */}
          <section className={styles.section}>
            <div className={styles.sectionGrid}>
              <ScrollAnimation>
                <div className={styles.content}>
                  <div className={styles.iconBadge}>
                    <Send size={24} strokeWidth={2} />
                  </div>
                  <h2 className={styles.sectionTitle}>
                    {t('contact.form.title', 'Send Us a Message')}
                  </h2>
                  <p className={styles.sectionDescription}>
                    {t('contact.form.description', 'Fill out the form and we\'ll get back to you within 24 hours. For urgent matters, use live chat.')}
                  </p>
                  <ContactForm />
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.imageContainer}>
                  <img
                    src="/images/contact-team-collaboration.jpg"
                    alt={t('contact.form.image_alt', 'Team collaboration')}
                    className={styles.image}
                    loading="lazy"
                  />
                </div>
              </ScrollAnimation>
            </div>
          </section>

          {/* Section 2: Email Us (Image LEFT, Content RIGHT) */}
          <section className={`${styles.section} ${styles.sectionAlt}`}>
            <div className={styles.sectionGrid}>
              <ScrollAnimation>
                <div className={styles.imageContainer}>
                  <img
                    src="/images/email.png"
                    alt={t('contact.email.image_alt', 'Email illustration')}
                    className={styles.image}
                    loading="lazy"
                  />
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.content}>
                  <div className={styles.iconBadge}>
                    <Mail size={24} strokeWidth={2} />
                  </div>
                  <h2 className={styles.sectionTitle}>
                    {t('contact.email.title', 'Prefer Email?')}
                  </h2>
                  <p className={styles.sectionDescription}>
                    {t('contact.email.description', 'Send us an email directly and we\'ll respond within one business day. Perfect for detailed questions or document sharing.')}
                  </p>
                  
                  <div className={styles.emailList}>
                    <div className={styles.emailItem}>
                      <span className={styles.emailLabel}>
                        {t('contact.email.general_label', 'General Inquiries')}
                      </span>
                      <a href="mailto:info@allegoryinsurance.com" className={styles.emailLink}>
                        info@allegoryinsurance.com
                      </a>
                    </div>
                    <div className={styles.emailItem}>
                      <span className={styles.emailLabel}>
                        {t('contact.email.support_label', 'Technical Support')}
                      </span>
                      <a href="mailto:support@allegoryinsurance.com" className={styles.emailLink}>
                        support@allegoryinsurance.com
                      </a>
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          </section>

          {/* Section 3: Live Agent (Content LEFT, Image RIGHT) */}
          <section className={styles.section}>
            <div className={styles.sectionGrid}>
              <ScrollAnimation>
                <div className={styles.content}>
                  <div className={styles.iconBadge}>
                    <MessageCircle size={24} strokeWidth={2} />
                  </div>
                  <h2 className={styles.sectionTitle}>
                    {t('contact.chat.title', 'Need Help Right Now?')}
                  </h2>
                  <p className={styles.sectionDescription}>
                    {t('contact.chat.description', 'Chat with our AI assistant instantly. Get answers about your policies, coverage questions, or account help in real-time.')}
                  </p>

                  <ul className={styles.featureList}>
                    <li>{t('contact.chat.feature1', 'Instant responses')}</li>
                    <li>{t('contact.chat.feature2', 'Policy-specific answers')}</li>
                    <li>{t('contact.chat.feature3', 'Available 24/7')}</li>
                  </ul>

                  <button className={styles.chatButton} onClick={handleChatClick}>
                    <MessageCircle size={20} strokeWidth={2.5} />
                    {t('contact.chat.button', 'Start Live Chat')}
                  </button>
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.imageContainer}>
                  <img
                    src="/images/contact-ai-assistant.jpg"
                    alt={t('contact.chat.image_alt', 'AI assistant')}
                    className={styles.image}
                    loading="lazy"
                  />
                </div>
              </ScrollAnimation>
            </div>
          </section>
        </div>
      </PageContainer>
    </>
  );
};

export default Contact;